import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TenantBranchSetting } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * Reads and writes the tenant and branch setting values that back the administration settings page.
 * Tenant reads return the tenant defaults; branch reads return the effective values with any branch
 * override applied. Saves are per-setting rather than a whole-page submit so two administrators
 * editing different settings cannot overwrite each other.
 */
@Injectable({
    providedIn: 'root',
})
export class TenantBranchSettingApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Fetches the tenant-level setting values.
     *
     * @param tenantId - The tenant identifier
     * @returns The tenant defaults for every declared setting
     */
    getTenant(tenantId: string): Observable<TenantBranchSetting[]> {
        return this.http.get<TenantBranchSetting[]>(this.baseUrl(tenantId));
    }

    /**
     * Fetches the effective setting values for a branch, each flagged with whether the branch is
     * overriding the tenant value.
     *
     * @param tenantId - The tenant identifier
     * @param branchId - The branch identifier
     * @returns The effective values for the branch
     */
    getBranch(tenantId: string, branchId: string): Observable<TenantBranchSetting[]> {
        return this.http.get<TenantBranchSetting[]>(`${this.baseUrl(tenantId)}/branch/${branchId}`);
    }

    /**
     * Sets a tenant-level setting value.
     *
     * @param tenantId - The tenant identifier
     * @param key - The setting display name identifying the setting
     * @param value - The value to persist, serialised as a string
     * @returns The persisted setting
     */
    saveTenant(tenantId: string, key: string, value: string): Observable<TenantBranchSetting> {
        return this.http.put<TenantBranchSetting>(this.baseUrl(tenantId), { key, value });
    }

    /**
     * Sets a branch-level override for a setting.
     *
     * @param tenantId - The tenant identifier
     * @param branchId - The branch identifier
     * @param key - The setting display name identifying the setting
     * @param value - The value to persist, serialised as a string
     * @returns The persisted setting
     */
    saveBranch(tenantId: string, branchId: string, key: string, value: string): Observable<TenantBranchSetting> {
        return this.http.put<TenantBranchSetting>(`${this.baseUrl(tenantId)}/branch/${branchId}`, { key, value });
    }

    /**
     * Clears a branch override so the branch inherits the tenant value again. The key is URL-encoded
     * because it is a display name and can contain characters that would otherwise alter the route.
     *
     * @param tenantId - The tenant identifier
     * @param branchId - The branch identifier
     * @param key - The setting display name identifying the setting
     * @returns The empty removal response
     */
    clearBranchOverride(tenantId: string, branchId: string, key: string): Observable<object> {
        return this.http.delete(`${this.baseUrl(tenantId)}/branch/${branchId}/${encodeURIComponent(key)}`);
    }

    private baseUrl(tenantId: string): string {
        return this.serviceUtils.getBaseUrl('TenantBranchSetting', tenantId);
    }
}
